import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { autoAppyUrl } from "src/config/autoAppyUrls";
import workspaceService from "src/services/workspaceService";
import workspaceWhatsappAccountService from "src/services/workspaceWhatsappAccountService";
import workspaceWhatsappPhoneNumberService from "src/services/workspaceWhatsappPhoneNumberService";
import userSettingsService from "src/services/userSettingsService";
import { storeConnection } from "src/common/whatsappConnection";
import { clearRequestCache } from "src/common/requestCache";
import { AuthContext } from "src/context/AuthContext";
import axios from "src/common/axiosInstance";

const EMPTY_WORKSPACE_FORM = {
  name: "",
};

const EMPTY_ACCOUNT_FORM = {
  business_name: "",
  business_id: "",
  waba_id: "",
  app_id: "",
  access_token: "",
  app_secret: "",
  webhook_verify_token: "https://autoappy.com/callback",
};

const EMPTY_PHONE_FORM = {
  display_name: "",
  verified_name: "",
  phone_number: "",
  phone_number_id: "",
  quality_rating: "GREEN",
  verification_status: "Verified",
  status: "Connected",
};

function useWhatsAppConnection({ mode = "settings", onSetupComplete }) {
  const navigate = useNavigate();
  const { currentUser, refreshSession } = useContext(AuthContext) || {};
  const isSetupMode = mode === "setup";
  const accountRequest = useRef(0);
  const phoneRequest = useRef(0);
  const [workspaces, setWorkspaces] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [whatsappAccountId, setWhatsappAccountId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [useAsDefault, setUseAsDefault] = useState(false);
  const [modal, setModal] = useState(null);
  const [workspaceForm, setWorkspaceForm] = useState(EMPTY_WORKSPACE_FORM);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT_FORM);
  const [phoneForm, setPhoneForm] = useState(EMPTY_PHONE_FORM);
  const [accountModalMode, setAccountModalMode] = useState("create");
  const [editingAccountId, setEditingAccountId] = useState(null);

  const loadPhoneNumbers = useCallback(
    async (selectedWorkspaceId, selectedAccountId, cachedPhoneId = null) => {
      const requestId = ++phoneRequest.current;
      try {
        setLoadingPhones(true);
        setError("");
        setPhoneNumbers([]);
        setPhoneNumberId("");

        const response = await workspaceWhatsappPhoneNumberService.index(
          selectedWorkspaceId,
          selectedAccountId,
        );
        if (requestId !== phoneRequest.current) return;
        const data = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(data) ? data : [];

        setPhoneNumbers(list);

        if (!list.length) return;

        const cachedPhone = list.find(
          (phone) => String(phone.id) === String(cachedPhoneId),
        );
        const selectedPhone = cachedPhone || list[0];
        setPhoneNumberId(selectedPhone.id);
      } catch (error) {
        if (requestId !== phoneRequest.current) return;
        console.error(error);
        setError(
          error?.response?.data?.message || "Unable to load phone numbers.",
        );
      } finally {
        if (requestId === phoneRequest.current) setLoadingPhones(false);
      }
    },
    [],
  );

  const loadAccounts = useCallback(
    async (
      selectedWorkspaceId,
      cachedAccountId = null,
      cachedPhoneId = null,
    ) => {
      const requestId = ++accountRequest.current;
      phoneRequest.current += 1;
      setLoadingPhones(false);
      try {
        setLoadingAccounts(true);
        setError("");
        setAccounts([]);
        setPhoneNumbers([]);
        setWhatsappAccountId("");
        setPhoneNumberId("");

        const response =
          await workspaceWhatsappAccountService.index(selectedWorkspaceId);
        if (requestId !== accountRequest.current) return;
        const data = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(data) ? data : [];

        setAccounts(list);

        if (!list.length) return;

        const cachedAccount = list.find(
          (account) => String(account.id) === String(cachedAccountId),
        );
        const selectedAccount = cachedAccount || list[0];
        setWhatsappAccountId(selectedAccount.id);
        await loadPhoneNumbers(
          selectedWorkspaceId,
          selectedAccount.id,
          cachedPhoneId,
        );
      } catch (error) {
        if (requestId !== accountRequest.current) return;
        console.error(error);
        setError(
          error?.response?.data?.message || "Unable to load WhatsApp accounts.",
        );
      } finally {
        if (requestId === accountRequest.current) setLoadingAccounts(false);
      }
    },
    [loadPhoneNumbers],
  );

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const settings = await userSettingsService.get();
      const cachedSetup = storeConnection(settings?.data?.data) || {};
      if (isSetupMode && cachedSetup.phoneNumberId) {
        onSetupComplete?.(cachedSetup);
        navigate("/dashboard", { replace: true });
        return;
      }
      clearRequestCache("/workspaces");
      const response = await workspaceService.index();
      const data = response?.data?.data ?? response?.data ?? [];
      const list = Array.isArray(data) ? data : [];

      setWorkspaces(list);

      if (!list.length) {
        setWorkspaceId("");
        setAccounts([]);
        setPhoneNumbers([]);
        setWhatsappAccountId("");
        setPhoneNumberId("");
        return;
      }

      const cachedWorkspace = list.find(
        (workspace) => String(workspace.id) === String(cachedSetup.workspaceId),
      );
      const selectedWorkspace = list.find((workspace) => workspace.id === currentUser?.local_workspace_id)
        || cachedWorkspace || (list.length === 1 && !currentUser?.current_workspace?.id && (currentUser?.workspaces?.length || 0) <= 1 ? list[0] : null);
      if (!selectedWorkspace) {
        setWorkspaceId(""); setAccounts([]); setPhoneNumbers([]); setWhatsappAccountId(""); setPhoneNumberId("");
        return;
      }
      setWorkspaceId(selectedWorkspace.id);

      await loadAccounts(
        selectedWorkspace.id,
        cachedSetup.whatsappAccountId,
        cachedSetup.phoneNumberId,
      );
    } catch (error) {
      console.error(error);

      setError(error?.response?.data?.message || "Unable to load settings.");
    } finally {
      setLoading(false);
    }
  }, [isSetupMode, onSetupComplete, navigate, loadAccounts, currentUser?.local_workspace_id, currentUser?.current_workspace?.id, currentUser?.workspaces?.length]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleWorkspaceChange = async (id) => {
    setUseAsDefault(false);
    setSaved(false);
    setError("");
    try {
      await axios.post('/api/auth/workspace', { workspace_id: id });
      clearRequestCache();
      setWorkspaceId(id);
      await loadAccounts(id);
      await refreshSession?.();
    } catch (e) { setError(e.response?.data?.message || 'Unable to switch organisation. Try again.'); }
  };

  const handleAccountChange = async (id) => {
    setSaved(false);
    setError("");
    setWhatsappAccountId(id);
    await loadPhoneNumbers(workspaceId, id);
  };

  const handlePhoneNumberChange = (id) => {
    setSaved(false);
    setError("");
    setPhoneNumberId(id);
  };

  const handleCreateWorkspace = () =>
    window.location.assign(autoAppyUrl("/organisation"));

  const handleCreateAccount = async (event) => {
    event.preventDefault();

    if (!workspaceId) {
      setError("Please select a workspace first.");
      return;
    }

    const requiredFields = [
      "business_name",
      "business_id",
      "access_token",
      "app_secret",
      "webhook_verify_token",
    ];

    const missingField = requiredFields.find(
      (field) => !accountForm[field]?.trim(),
    );

    if (missingField) {
      setError(
        "Business name, Business ID, Access Token and App Secret are required.",
      );
      return;
    }

    try {
      setCreating(true);
      setError("");

      const response = await workspaceWhatsappAccountService.create(
        workspaceId,
        {
          business_name: accountForm.business_name.trim(),
          business_id: accountForm.business_id.trim(),
          waba_id: accountForm.waba_id?.trim() || null,
          app_id: accountForm.app_id?.trim() || null,
          access_token: accountForm.access_token.trim(),
          app_secret: accountForm.app_secret.trim(),
          webhook_verify_token: accountForm.webhook_verify_token.trim(),
        },
      );

      const account = response?.data?.data ?? response?.data;

      if (!account?.id) {
        throw new Error("WhatsApp account ID was not returned by the server.");
      }

      setAccounts((previous) => [...previous, account]);
      setWhatsappAccountId(account.id);
      setPhoneNumbers([]);
      setPhoneNumberId("");
      setAccountForm({ ...EMPTY_ACCOUNT_FORM });
      setModal(null);
    } catch (error) {
      console.error(error);
      setError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to create WhatsApp account.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateAccount = async (event) => {
    event.preventDefault();

    if (!editingAccountId) return;

    if (
      !accountForm.business_name?.trim() ||
      !accountForm.business_id?.trim()
    ) {
      setError("Business name and Business ID are required.");
      return;
    }

    try {
      setCreating(true);
      setError("");

      const payload = {
        business_name: accountForm.business_name.trim(),
        business_id: accountForm.business_id.trim(),
        waba_id: accountForm.waba_id?.trim() || "",
        app_id: accountForm.app_id?.trim() || "",
      };

      // Secrets: only send when the operator actually typed a new value.
      if (accountForm.access_token?.trim()) {
        payload.access_token = accountForm.access_token.trim();
      }
      if (accountForm.app_secret?.trim()) {
        payload.app_secret = accountForm.app_secret.trim();
      }

      const response = await workspaceWhatsappAccountService.update(
        workspaceId,
        editingAccountId,
        payload,
      );

      const updated = response?.data?.data ?? response?.data ?? {};

      setAccounts((previous) =>
        previous.map((item) =>
          String(item.id) === String(editingAccountId)
            ? { ...item, ...updated }
            : item,
        ),
      );

      setAccountForm({ ...EMPTY_ACCOUNT_FORM });
      setEditingAccountId(null);
      setAccountModalMode("create");
      setModal(null);
    } catch (error) {
      console.error(error);
      setError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update WhatsApp account.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePhoneNumber = async (event) => {
    event.preventDefault();

    if (!workspaceId) {
      setError("Please select a workspace first.");
      return;
    }

    if (!whatsappAccountId) {
      setError("Please select a WhatsApp account first.");
      return;
    }

    const requiredFields = [
      "display_name",
      "verified_name",
      "phone_number",
      "phone_number_id",
    ];

    const missingField = requiredFields.find(
      (field) => !phoneForm[field]?.trim(),
    );

    if (missingField) {
      setError("Please complete all phone number fields.");
      return;
    }

    try {
      setCreating(true);
      setError("");

      const response = await workspaceWhatsappPhoneNumberService.create(
        workspaceId,
        whatsappAccountId,
        {
          display_name: phoneForm.display_name.trim(),
          verified_name: phoneForm.verified_name.trim(),
          phone_number: phoneForm.phone_number.trim(),
          phone_number_id: phoneForm.phone_number_id.trim(),
          quality_rating: phoneForm.quality_rating,
          verification_status: phoneForm.verification_status,
          status: phoneForm.status,
        },
      );

      const phone = response?.data?.data ?? response?.data;

      if (!phone?.id) {
        throw new Error("Phone number ID was not returned by the server.");
      }

      setPhoneNumbers((previous) => [...previous, phone]);
      setPhoneNumberId(phone.id);
      setPhoneForm({ ...EMPTY_PHONE_FORM });
      setModal(null);
      navigate("/settings");
    } catch (error) {
      console.error(error);
      setError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to create phone number.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!workspaceId || !whatsappAccountId || !phoneNumberId) {
      setError("Please select a complete WhatsApp connection.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSaved(false);

      await userSettingsService.update({
        active_workspace_id: workspaceId,
        active_whatsapp_account_id: whatsappAccountId,
        active_phone_number_id: phoneNumberId,
        ...(useAsDefault && activeWorkspace?.can_set_default_connection
          ? { use_as_workspace_default: true }
          : {}),
      });
      const setupData = storeConnection({
        active_workspace_id: workspaceId,
        active_whatsapp_account_id: whatsappAccountId,
        active_phone_number_id: phoneNumberId,
      });
      await refreshSession?.();
      if (useAsDefault && activeWorkspace?.can_set_default_connection) {
        setWorkspaces((previous) =>
          previous.map((workspace) =>
            workspace.id === workspaceId
              ? {
                  ...workspace,
                  default_whatsapp_phone_number_id: phoneNumberId,
                }
              : workspace,
          ),
        );
        setUseAsDefault(false);
      }

      setSaved(true);

      if (isSetupMode) {
        onSetupComplete?.(setupData);
        setTimeout(() => {
          navigate("/dashboard", {
            replace: true,
          });
        }, 500);
        return;
      }

      setTimeout(() => {
        setSaved(false);
      }, 3000);
    } catch (error) {
      console.error(error);
      setError(
        error?.response?.data?.message ||
          "Unable to save configuration. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openWorkspaceModal = () =>
    window.location.assign(autoAppyUrl("/organisation"));

  const openAccountModal = () => {
    setError("");
    setAccountForm({ ...EMPTY_ACCOUNT_FORM });
    setAccountModalMode("create");
    setEditingAccountId(null);
    setModal("account");
  };

  const openEditAccountModal = (account) => {
    if (!account) return;
    setError("");
    setEditingAccountId(account.id);
    setAccountModalMode("edit");
    setAccountForm({
      ...EMPTY_ACCOUNT_FORM,
      business_name: account.business_name || "",
      business_id: account.business_id || "",
      waba_id: account.waba_id || "",
      app_id: account.app_id || "",
      access_token: "",
      app_secret: "",
    });
    setModal("account");
  };

  const openPhoneModal = () => {
    setError("");
    setPhoneForm({ ...EMPTY_PHONE_FORM });
    setModal("phone");
  };

  const activeWorkspace = workspaces.find(
    (item) => String(item.id) === String(workspaceId),
  );
  const activeAccount = accounts.find(
    (item) => String(item.id) === String(whatsappAccountId),
  );
  const activePhone = phoneNumbers.find(
    (item) => String(item.id) === String(phoneNumberId),
  );
  const workspaceName =
    activeWorkspace?.name || activeWorkspace?.workspace_name || "Workspace";
  const accountName =
    activeAccount?.business_name || activeAccount?.name || "WhatsApp Business";
  const phoneName =
    activePhone?.verified_name ||
    activePhone?.display_name ||
    "WhatsApp Business Number";
  const phoneNumber =
    activePhone?.display_phone_number ||
    activePhone?.phone_number ||
    activePhone?.number ||
    "Phone number";

  return {
    isSetupMode,
    reload: loadInitialData,
    canManageConnection: Boolean(activeWorkspace?.can_manage_connection),
    canSetDefaultConnection: Boolean(
      activeWorkspace?.can_set_default_connection,
    ),
    isDefaultConnection:
      activeWorkspace?.default_whatsapp_phone_number_id === phoneNumberId,
    useAsDefault,
    setUseAsDefault,
    hasConnection: Boolean(activeWorkspace && activeAccount && activePhone),
    connectionStatus:
      (activeAccount?.status && activeAccount.status !== "Connected"
        ? activeAccount.status
        : activePhone?.status) || "Not connected",
    workspaces,
    accounts,
    phoneNumbers,
    workspaceId,
    whatsappAccountId,
    phoneNumberId,
    loading,
    loadingAccounts,
    loadingPhones,
    saving,
    creating,
    error,
    saved,
    modal,
    workspaceForm,
    accountForm,
    phoneForm,
    accountModalMode,
    editingAccountId,
    workspaceName,
    accountName,
    phoneName,
    phoneNumber,
    setError,
    setModal,
    setWorkspaceForm,
    setAccountForm,
    setPhoneForm,
    handleWorkspaceChange,
    handleAccountChange,
    handlePhoneNumberChange,
    handleCreateWorkspace,
    handleCreateAccount,
    handleUpdateAccount,
    handleCreatePhoneNumber,
    handleSave,
    openWorkspaceModal,
    openAccountModal,
    openEditAccountModal,
    openPhoneModal,
  };
}

export default useWhatsAppConnection;
